import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import LoginPage from '@/pages/LoginPage';
import NoAccessPage from '@/pages/NoAccessPage';
import SignupPage from '@/pages/SignupPage';

const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const WalletPage = React.lazy(() => import('@/pages/WalletPage'));
const ProductsPage = React.lazy(() => import('@/pages/ProductsPage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const ConversionTrackingPage = React.lazy(() => import('@/pages/ConversionTrackingPage'));
const PendingApprovalPage = React.lazy(() => import('@/pages/PendingApprovalPage'));
const AdminAdvertisersPage = React.lazy(() => import('@/pages/admin/AdvertisersPage'));
const AdminProductsReviewPage = React.lazy(() => import('@/pages/admin/ProductsReviewPage'));
const AdminKeywordReviewPage = React.lazy(() => import('@/pages/admin/KeywordReviewPage'));
const AdminEarningsPage = React.lazy(() => import('@/pages/admin/EarningsPage'));
const AdminPlatformReportingPage = React.lazy(() => import('@/pages/admin/PlatformReportingPage'));
const AdminEmailPreviewPage = React.lazy(() => import('@/pages/admin/EmailPreviewPage'));
const DashboardLayout = React.lazy(() => import('@/components/layout/DashboardLayout'));

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

// Gates any route behind: signed in + (is advertiser with active status) OR (super_admin).
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdvertiser, isSuperAdmin, advertiser } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperAdmin) return <>{children}</>;
  if (!isAdvertiser) return <Navigate to="/no-access" replace />;
  if (!advertiser) return <Navigate to="/no-access" replace />;
  if (advertiser.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }
  if (advertiser.status === 'suspended' || advertiser.status === 'archived') {
    return <Navigate to="/no-access" replace />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isSuperAdmin } = useAuth();
  if (loading) return <Spinner />;
  if (!user || !isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/apply" element={<Navigate to="/signup" replace />} />
        <Route path="/no-access" element={<NoAccessPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="wallet" element={<WalletPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="conversion-tracking" element={<ConversionTrackingPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route
            path="admin/advertisers"
            element={
              <AdminRoute>
                <AdminAdvertisersPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/products"
            element={
              <AdminRoute>
                <AdminProductsReviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/keyword-review"
            element={
              <AdminRoute>
                <AdminKeywordReviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/earnings"
            element={
              <AdminRoute>
                <AdminEarningsPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/platform-reporting"
            element={
              <AdminRoute>
                <AdminPlatformReportingPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/email-preview"
            element={
              <AdminRoute>
                <AdminEmailPreviewPage />
              </AdminRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  // ErrorBoundary sits INSIDE Router so it can read the pathname and auto-
  // reset on route changes — otherwise a single render error poisons every
  // subsequent route until the user hits "Return to Dashboard".
  return (
    <Router>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Router>
  );
}
